/*
 * Sportstech Feature Slider
 * Architektur: Single-Source-of-Truth
 *  - Scroll ist die einzige Quelle der Wahrheit für activeIndex
 *  - Alle Navigationen (Arrow/Dot/Click/Swipe) fließen über scrollTo
 *  - Play/Pause wird ausschließlich durch Active-State-Reconcile getriggert
 *  - Genau ein Play-Pfad: Theme's vm.player.play() Proxy
 */
(function () {
  if (window.__STFeatureSliderInit) return;
  window.__STFeatureSliderInit = true;

  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(ctx, args), wait);
    };
  }

  // ST 2026-10-24 (Wrike 4591202137): [data-load-visible] videos (snippets/st-video-load-visible) keep autoplay, but only
  // the ACTIVE slide loads, and only while the slider is visible: no prime/warm-up of the other slides, a slide that
  // loses "active" or the slider leaving the screen pauses it and stops its download (theme.js stopLoading).
  const loadVisible = (vm) => vm.hasAttribute('data-load-visible');

  class STFeatureSlider extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;

      this.viewport = this.querySelector('[data-st-fslider-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-st-fslider-slide]'));
      this.prevBtn = this.querySelector('[data-st-fslider-prev]');
      this.nextBtn = this.querySelector('[data-st-fslider-next]');
      this.dots = Array.from(this.querySelectorAll('[data-st-fslider-dot]'));
      this.dotsNav = this.dots.length ? this.dots[0].closest('nav') : null;
      this.arrowsNav = this.querySelector('.st-fslider__nav'); // Pfeil-Container (Header)

      this.loop = this.hasAttribute('data-loop');
      this.autoAdvance = parseInt(this.getAttribute('data-auto-advance'), 10) || 0;
      this.activeIndex = -1;     // wird durch ersten reconcile gesetzt
      // maxIndex = höchste erreichbare Scroll-Position (= Anzahl „Seiten" - 1). Bei >1 Karte/View
      // liegen die letzten Slides hinter maxScroll → sie bilden keine eigene Dot-Seite.
      this.maxIndex = this.slides.length - 1;
      this._isProgrammaticScroll = false;  // sperrt reconcile während go()-Animation
      this._inViewport = false;

      if (!this.viewport || this.slides.length === 0) return;

      // Deterministischer Initial-Scroll-Zustand
      this.viewport.scrollLeft = 0;

      // Dots Seiten-basiert: überzählige ausblenden (Klick-/Play-Logik bleibt unberührt).
      this._recalcBounds();
      this._updateDots();
      requestAnimationFrame(() => { this._recalcBounds(); this._updateDots(); });
      this._onResize = debounce(() => { this._recalcBounds(); this._updateDots(); }, 150);
      window.addEventListener('resize', this._onResize);

      // ─── Arrow / Dot / Click-Nav ──────────────────────────
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.activeIndex - 1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.activeIndex + 1));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));

      this.slides.forEach((slide, i) => {
        const toggle = slide.querySelector('[data-st-fslider-toggle]');
        if (toggle) toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          this._toggleActiveVideo();
        });
        slide.addEventListener('click', (e) => {
          if (i === this.activeIndex) return;
          if (e.target.closest('a, button, [role="button"]')) return;
          this.go(i);
        });
        slide.addEventListener('keydown', (e) => {
          if (i === this.activeIndex) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.go(i);
          }
        });
      });

      // ─── iOS-Autoplay-Unlock: beim ersten User-Gesture ALLE Videos synchron anstoßen.
      // iOS erlaubt nachfolgende play()-Calls nur auf Video-Elements, die mindestens einmal
      // innerhalb eines Gestures abgespielt wurden. Wir spielen kurz an + pausieren sofort.
      this._videosPrimed = false;
      const primeVideos = () => {
        if (this._videosPrimed) return;
        this._videosPrimed = true;
        this.slides.forEach((s, idx) => {
          const vm = s.querySelector('video-media');
          if (!vm || (loadVisible(vm) && idx !== this.activeIndex)) return;
          try { void vm.player; } catch (e) {} // erzwingt Template-Extraktion → <video> existiert jetzt
          const video = vm.querySelector('video');
          if (!video) return;
          video.muted = true;
          video.playsInline = true;
          try {
            const p = video.play();
            if (p && typeof p.then === 'function') {
              p.then(() => {
                // Alle außer den aktuell aktiven sofort wieder pausieren
                if (idx !== this.activeIndex) video.pause();
              }).catch(() => {});
            }
          } catch (e) {}
        });
      };
      // touchstart + click decken Mobile und Desktop ab; `once: true` via Flag-Check
      this.addEventListener('touchstart', primeVideos, { passive: true });
      this.addEventListener('click', primeVideos, { passive: true });

      // ─── Scroll-Reconcile: einzige Quelle für activeIndex ─
      const reconcile = () => {
        if (this._isProgrammaticScroll) return;
        const idx = this._closestIndex();
        if (idx !== this.activeIndex) this._setActive(idx);
      };
      this._settleReconcile = debounce(reconcile, 120);
      this._onScroll = () => this._settleReconcile();
      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      if ('onscrollend' in window) {
        this._onScrollEnd = reconcile;
        this.viewport.addEventListener('scrollend', this._onScrollEnd, { passive: true });
      }

      // ─── Video-Controls hartnäckig unterdrücken (iOS fallback) ─
      this._controlsAttrObserver = new MutationObserver((mutations) => {
        mutations.forEach((m) => {
          if (m.type === 'attributes' && m.attributeName === 'controls' && m.target.hasAttribute('controls')) {
            m.target.removeAttribute('controls');
          }
        });
      });
      const killControls = (video) => {
        video.removeAttribute('controls');
        video.controls = false;
        try {
          video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback noplaybackrate');
          video.disablePictureInPicture = true;
          video.disableRemotePlayback = true;
        } catch (e) {}
        this._controlsAttrObserver.observe(video, { attributes: true, attributeFilter: ['controls'] });
      };
      this._slideChildObservers = [];
      this.slides.forEach((s) => {
        s.querySelectorAll('video').forEach(killControls);
        const co = new MutationObserver((muts) => {
          muts.forEach((m) => m.addedNodes.forEach((node) => {
            if (node.nodeType !== 1) return;
            if (node.tagName === 'VIDEO') killControls(node);
            else if (node.querySelectorAll) node.querySelectorAll('video').forEach(killControls);
          }));
        });
        co.observe(s, { childList: true, subtree: true });
        this._slideChildObservers.push(co);
      });

      // ─── Viewport-Entry: pausiert beim Leave. Play wird NICHT hier gesteuert. ─
      this._onVisibilityChange = () => {
        if (document.hidden) this._pauseAll();
        else if (this._inViewport && this.activeIndex >= 0) this._playIndex(this.activeIndex);
      };
      document.addEventListener('visibilitychange', this._onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            this._inViewport = e.isIntersecting;
            if (!e.isIntersecting) this._pauseAll();
            else this._resumeVisible();
          });
        }, { threshold: 0.25 });
        this._io.observe(this);
      } else {
        this._inViewport = true;
      }

      // ─── Auto-Advance (optional) ──────────────────────────
      if (this.autoAdvance > 0) {
        this._startTimer();
        this.addEventListener('mouseenter', () => this._stopTimer());
        this.addEventListener('mouseleave', () => this._startTimer());
        this.addEventListener('touchstart', () => this._stopTimer(), { passive: true });
        this.addEventListener('touchend', () => {
          clearTimeout(this._touchResumeTimer);
          this._touchResumeTimer = setTimeout(() => this._startTimer(), 3000);
        }, { passive: true });
      }

      // ─── Initial-State ─────────────────────────────────────
      // Explizit Index 0 setzen → Active-Styling + (nach IO-ready) Play.
      // Play des ersten Videos passiert entweder via autoplay-Attribut im DOM
      // (vom Theme's Motion.inView), oder beim ersten reconcile falls Section
      // bereits in View ist. Doppel-Play-Anfragen sind idempotent auf dem gleichen Video.
      this._setActive(0, { skipPlay: false });
    }

    disconnectedCallback() {
      this._stopTimer();
      clearTimeout(this._touchResumeTimer);
      if (this._io) this._io.disconnect();
      if (this._controlsAttrObserver) this._controlsAttrObserver.disconnect();
      if (this._slideChildObservers) this._slideChildObservers.forEach((co) => co.disconnect());
      if (this._onVisibilityChange) document.removeEventListener('visibilitychange', this._onVisibilityChange);
      if (this._onScroll && this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
      if (this._onScrollEnd && this.viewport) this.viewport.removeEventListener('scrollend', this._onScrollEnd);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    // ─── Navigation ─────────────────────────────────────────
    go(newIndex) {
      const n = this.slides.length;
      if (n === 0) return;
      if (this.loop) newIndex = (newIndex + n) % n;
      else newIndex = Math.max(0, Math.min(n - 1, newIndex));
      if (newIndex === this.activeIndex) return;

      const target = this.slides[newIndex];
      if (!target) return;

      // Scroll-Reconcile während programmatischer Animation sperren,
      // damit Mid-Scroll-Positionen nicht zu einem falschen Active führen.
      this._isProgrammaticScroll = true;
      clearTimeout(this._unlockTimer);

      // State SOFORT setzen → responsive Feeling (UI + Video folgt).
      this._setActive(newIndex);

      const left = target.offsetLeft;
      if (typeof this.viewport.scrollTo === 'function') {
        this.viewport.scrollTo({ left, behavior: 'smooth' });
      } else {
        this.viewport.scrollLeft = left;
      }

      // Lock nach Scroll-Ende lösen. scrollend falls verfügbar, sonst Fallback-Timeout.
      const unlock = () => { this._isProgrammaticScroll = false; };
      if ('onscrollend' in window) {
        const once = () => { this.viewport.removeEventListener('scrollend', once); unlock(); };
        this.viewport.addEventListener('scrollend', once, { passive: true });
        this._unlockTimer = setTimeout(unlock, 1200); // safety net
      } else {
        this._unlockTimer = setTimeout(unlock, 800);
      }
    }

    // ─── Reconcile-Helpers ──────────────────────────────────
    // Pro Slide wird die erwartete Snap-Position berechnet (abhängig von scroll-snap-align).
    // Tie-Breaker: bei gleichen Distanzen (Edge Case bei max-scroll, wo mehrere Snap-Positions
    // zusammenfallen) bevorzugen wir die aktuelle activeIndex → keine ungewollten Rücksprünge.
    _closestIndex() {
      const scrollLeft = this.viewport.scrollLeft;
      const vpWidth = this.viewport.clientWidth;
      const TOL = 1.5; // px Toleranz für Tie-Erkennung
      let best = 0, bestDist = Infinity;
      for (let i = 0; i < this.slides.length; i++) {
        const s = this.slides[i];
        const alignEnd = (getComputedStyle(s).scrollSnapAlign || '').indexOf('end') !== -1;
        const snapPos = alignEnd ? s.offsetLeft + s.offsetWidth - vpWidth : s.offsetLeft;
        const dist = Math.abs(snapPos - scrollLeft);
        if (dist < bestDist - TOL) {
          bestDist = dist;
          best = i;
        } else if (Math.abs(dist - bestDist) <= TOL && i === this.activeIndex) {
          // Tie mit aktuellem Active → Active behalten
          best = i;
        }
      }
      return best;
    }

    // Höchste erreichbare Scroll-Position (Slide, dessen offsetLeft noch <= maxScroll ist).
    _recalcBounds() {
      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      const TOL = 2;
      let last = 0;
      for (let i = 0; i < this.slides.length; i++) {
        if (this.slides[i].offsetLeft <= maxScroll + TOL) last = i;
      }
      this.maxIndex = last;
    }

    // Dots = Anzahl Scroll-Seiten. Überzählige ausblenden; bei nur 1 Seite ganze Dot-Nav weg.
    // Inline-display nötig, da .st-fslider__dots {display:flex} das [hidden]-Attribut überschreibt.
    _updateDots() {
      const showNav = this.maxIndex >= 1; // gibt es überhaupt was zu scrollen?
      // Dot-Nav UND Pfeile ausblenden, wenn nichts zu scrollen ist.
      [this.dotsNav, this.arrowsNav].forEach((el) => {
        if (!el) return;
        el.hidden = !showNav;
        el.style.display = showNav ? '' : 'none';
      });
      const current = this.activeIndex < 0 ? 0 : Math.min(this.activeIndex, this.maxIndex);
      this.dots.forEach((d, i) => {
        const hide = i > this.maxIndex;
        d.hidden = hide;
        d.style.display = hide ? 'none' : '';
        d.setAttribute('aria-current', i === current ? 'true' : 'false');
      });
    }

    _setActive(index, opts) {
      opts = opts || {};
      const prev = this.activeIndex;
      this.activeIndex = index;

      this.slides.forEach((s, i) => s.toggleAttribute('data-active', i === index));
      // Aktiver Dot = Scroll-Seite (geklemmt). activeIndex kann beim Klick auf eine ferne Karte
      // > maxIndex sein → dann leuchtet der letzte sichtbare Dot. Graying/Video bleibt bei index.
      this.dots.forEach((d, i) => d.setAttribute('aria-current', i === Math.min(index, this.maxIndex) ? 'true' : 'false'));
      if (this.prevBtn && !this.loop) this.prevBtn.toggleAttribute('disabled', index === 0);
      if (this.nextBtn && !this.loop) this.nextBtn.toggleAttribute('disabled', index === this.slides.length - 1);

      // Toggle-UI zurücksetzen (für Pause/Play-Button)
      this.slides.forEach((s) => {
        const t = s.querySelector('[data-st-fslider-toggle]');
        if (t) t.removeAttribute('data-paused');
      });

      if (prev !== index) {
        this._pauseIndex(prev);
        if (!opts.skipPlay) this._playIndex(index);
        this._warmAdjacent();
      }
    }

    _warmAdjacent() {
      const n = this.slides.length;
      const targets = this.loop
        ? [(this.activeIndex - 1 + n) % n, (this.activeIndex + 1) % n]
        : [this.activeIndex - 1, this.activeIndex + 1].filter(i => i >= 0 && i < n);
      targets.forEach((i) => {
        const vm = this.slides[i].querySelector('video-media');
        if (!vm || vm.playerProxy || loadVisible(vm)) return;
        try { void vm.player; } catch (e) {}
      });
    }

    // ─── Einziger Play/Pause-Pfad ───────────────────────────
    _playIndex(index) {
      if (index < 0 || index >= this.slides.length) return;
      const vm = this.slides[index].querySelector('video-media');
      if (!vm) return;
      if (loadVisible(vm)) {
        // theme.js DeferredMedia may play it on its own (in-view) only while it is the active slide.
        vm.paused = false;
        if (!this._inViewport) {
          vm.setAttribute('autoplay', '');
          return;
        }
      }
      try {
        vm.setAttribute('autoplay', '');
        if (vm.player && typeof vm.player.play === 'function') vm.player.play();
      } catch (e) {}
    }

    _pauseIndex(index) {
      if (index < 0 || index >= this.slides.length) return;
      const vm = this.slides[index].querySelector('video-media');
      if (!vm) return;
      vm.removeAttribute('autoplay');
      if (loadVisible(vm)) {
        vm.paused = true; // keeps theme.js in-view logic from starting a non-active slide
        if (!vm.playerProxy) return; // never created → nothing loaded, nothing to pause
      }
      try {
        if (vm.player && typeof vm.player.pause === 'function') vm.player.pause();
        else {
          const v = vm.querySelector('video');
          if (v && !v.paused) v.pause();
        }
      } catch (e) {}
      if (loadVisible(vm)) vm.stopLoading?.();
    }

    // Load-when-visible: the slider (re)entered the screen → the active slide starts, unless the user paused it.
    _resumeVisible() {
      const slide = this.slides[this.activeIndex];
      const vm = slide?.querySelector('video-media');
      if (!vm || !loadVisible(vm)) return;
      if (slide.querySelector('[data-st-fslider-toggle][data-paused]')) return;
      this._playIndex(this.activeIndex);
    }

    _pauseAll() {
      for (let i = 0; i < this.slides.length; i++) this._pauseIndex(i);
    }

    // ─── Toggle-Button auf aktivem Video ────────────────────
    _toggleActiveVideo() {
      const vm = this.slides[this.activeIndex]?.querySelector('video-media');
      const toggle = this.slides[this.activeIndex]?.querySelector('[data-st-fslider-toggle]');
      if (!vm) return;
      try {
        if (vm.hasAttribute('playing')) {
          if (vm.player && typeof vm.player.pause === 'function') vm.player.pause();
          if (toggle) {
            toggle.setAttribute('data-paused', '');
            const lbl = toggle.getAttribute('data-play-label');
            if (lbl) toggle.setAttribute('aria-label', lbl);
          }
        } else {
          if (vm.player && typeof vm.player.play === 'function') vm.player.play();
          if (toggle) {
            toggle.removeAttribute('data-paused');
            const lbl = toggle.getAttribute('data-pause-label');
            if (lbl) toggle.setAttribute('aria-label', lbl);
          }
        }
      } catch (e) {}
    }

    // ─── Auto-Advance-Timer ─────────────────────────────────
    _startTimer() {
      if (!this.autoAdvance) return;
      this._stopTimer();
      this._timer = window.setInterval(() => this.go(this.activeIndex + 1), this.autoAdvance);
    }
    _stopTimer() {
      if (this._timer) { window.clearInterval(this._timer); this._timer = null; }
    }
  }

  if (!customElements.get('st-feature-slider')) {
    customElements.define('st-feature-slider', STFeatureSlider);
  }
})();
